import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getStoredConsent,
  saveConsent,
  withdrawAllConsent,
  CONSENT_OPEN_EVENT,
  CONSENT_EVENT_NAME,
  ConsentRecord,
  ConsentPurposes,
} from '../utils/consentManager';

// Clean, zero-dependency inline SVGs to guarantee 100% build & TypeScript compatibility
const ShieldIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SlidersIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const AnalyticsIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const EnvelopeIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const UndoIcon: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a5 5 0 015 5v2m0 0l-3-3m3 3l3-3M3 10l3 3m-3-3l3-3" />
  </svg>
);

const InfoIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TargetIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FolderIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ScaleIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

export const ConsentBanner: React.FC = () => {
  // State: whether initial banner is shown (visible on page open as requested)
  const [showBanner, setShowBanner] = useState<boolean>(true);

  // State: whether preferences / privacy notice modal is open
  // Auto-opens if URL hash matches #revealed-modal or #dpdp-consent-modal
  const [showPreferences, setShowPreferences] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return (
        window.location.hash === '#revealed-modal' ||
        window.location.hash === '#dpdp-consent-modal'
      );
    }
    return false;
  });

  // Current toggle states
  const [toggles, setToggles] = useState<ConsentPurposes>({
    necessary: true,
    analytics: false,
    communications: false,
  });

  // Stored consent record
  const [currentConsent, setCurrentConsent] = useState<ConsentRecord | null>(null);

  // Success message after updating preferences
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Safe helper to close modal and clear hash if opened via #revealed-modal
  const handleCloseModal = useCallback(() => {
    setShowPreferences(false);
    if (typeof window !== 'undefined') {
      if (
        window.location.hash === '#revealed-modal' ||
        window.location.hash === '#dpdp-consent-modal'
      ) {
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch {
          window.location.hash = '';
        }
      }
    }
  }, []);

  // Safe helper to open modal and set #revealed-modal hash
  const handleOpenPreferencesModal = useCallback(() => {
    const active = getStoredConsent();
    if (active) {
      setToggles(active.purposes);
    }
    if (typeof window !== 'undefined' && window.location.hash !== '#revealed-modal') {
      window.location.hash = 'revealed-modal';
    }
    setShowPreferences(true);
  }, []);

  // Initialize consent state on page mount and set up listeners
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setCurrentConsent(stored);
      setToggles(stored.purposes);
    } else {
      // Ensure defaults are strictly OFF for optional purposes
      setToggles({
        necessary: true,
        analytics: false,
        communications: false,
      });
    }

    // Ensure banner is visible initially
    setShowBanner(true);

    // Check URL hash immediately on mount
    if (
      typeof window !== 'undefined' &&
      (window.location.hash === '#revealed-modal' || window.location.hash === '#dpdp-consent-modal')
    ) {
      setShowPreferences(true);
    }

    // Hash change listener (handles navigation to #revealed-modal)
    const handleHashChange = () => {
      if (
        window.location.hash === '#revealed-modal' ||
        window.location.hash === '#dpdp-consent-modal'
      ) {
        const active = getStoredConsent();
        if (active) setToggles(active.purposes);
        setShowPreferences(true);
      }
    };

    // Listen for external trigger to open preferences (e.g. from footer link)
    const handleExternalOpen = () => {
      handleOpenPreferencesModal();
    };

    // Listen for external consent updates or withdrawals (e.g. from persistent footer buttons)
    const handleConsentUpdated = (e: Event) => {
      const customEvt = e as CustomEvent<ConsentRecord>;
      if (customEvt && customEvt.detail) {
        setCurrentConsent(customEvt.detail);
        setToggles(customEvt.detail.purposes);
        setShowPreferences(false);
        setShowBanner(false);
        if (customEvt.detail.status === 'withdrawn') {
          showToast('All optional consents have been withdrawn (§6(4) DPDPA). Future optional processing is disabled.');
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener(CONSENT_OPEN_EVENT, handleExternalOpen);
    window.addEventListener(CONSENT_EVENT_NAME, handleConsentUpdated);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener(CONSENT_OPEN_EVENT, handleExternalOpen);
      window.removeEventListener(CONSENT_EVENT_NAME, handleConsentUpdated);
    };
  }, [handleOpenPreferencesModal]);

  // Handler: Accept All
  const handleAcceptAll = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: true,
        communications: true,
      },
      'accepted_all'
    );
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowBanner(false);
    handleCloseModal();
    showToast('Your privacy preferences have been saved (All purposes accepted).');
  }, [handleCloseModal]);

  // Handler: Reject All Optional Purposes
  const handleRejectAll = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: false,
        communications: false,
      },
      'rejected_all'
    );
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowBanner(false);
    handleCloseModal();
    showToast('Only strictly necessary cookies will be used. Optional processing declined.');
  }, [handleCloseModal]);

  // Handler: Save Granular Preferences
  const handleSavePreferences = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: toggles.analytics,
        communications: toggles.communications,
      },
      'custom'
    );
    setCurrentConsent(updated);
    setShowBanner(false);
    handleCloseModal();
    showToast('Your custom privacy choices have been successfully updated.');
  }, [toggles, handleCloseModal]);

  // Handler: Withdraw All Consent (CFE-06 & §6(4) Withdrawal Symmetry)
  const handleWithdrawAll = useCallback(() => {
    const updated = withdrawAllConsent();
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowBanner(false);
    handleCloseModal();
    showToast('All optional consents have been withdrawn (§6(4) DPDPA). Future optional processing is disabled.');
  }, [handleCloseModal]);

  // Handler for toggle changes (type-safe for optional categories)
  const handleToggleChange = (category: 'analytics' | 'communications') => {
    setToggles((prev) => ({
      ...prev,
      analytics: category === 'analytics' ? !prev.analytics : prev.analytics,
      communications: category === 'communications' ? !prev.communications : prev.communications,
    }));
  };

  // Keyboard accessibility: Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPreferences) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPreferences, handleCloseModal]);

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-[9999] bg-gray-900 text-white px-5 py-3 rounded-lg shadow-2xl flex items-center space-x-3 text-sm border-l-4 border-primary animate-fadeIn"
        >
          <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Floating Privacy Badge (Persistent Withdrawal & Management Mechanism) */}
      {!showBanner && !showPreferences && (
        <button
          type="button"
          onClick={handleOpenPreferencesModal}
          className="fixed bottom-5 left-5 z-40 bg-white hover:bg-gray-50 text-primary border border-gray-300 hover:border-primary shadow-lg hover:shadow-xl rounded-full p-3 flex items-center justify-center transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          title="Privacy & Consent Settings"
          aria-label="Manage Consent Preferences"
          id="dpdp-privacy-badge"
        >
          <ShieldIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-300 ease-in-out text-xs font-semibold text-gray-800 ml-0 group-hover:ml-2">
            Privacy Choices
          </span>
        </button>
      )}

      {/* 1. Global Consent Banner */}
      {showBanner && !showPreferences && (
        <aside
          role="dialog"
          aria-modal="true"
          aria-labelledby="dpdp-banner-title"
          aria-describedby="dpdp-banner-desc"
          id="dpdp-consent-banner"
          data-dpdp="banner"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t-2 border-primary shadow-2xl transition-all duration-300 animate-slideUp font-poppins text-gray-900"
        >
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Notice text */}
              <div className="flex-1 pr-0 lg:pr-6">
                <div className="flex items-center space-x-2.5 mb-2">
                  <div className="p-1.5 bg-primary/10 rounded-md text-primary">
                    <ShieldIcon className="w-4 h-4" />
                  </div>
                  <h2
                    id="dpdp-banner-title"
                    className="text-base sm:text-lg font-bold text-gray-900 tracking-tight"
                  >
                    Your Privacy &amp; Consent Choices
                  </h2>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                    DPDP Act, 2023
                  </span>
                </div>
                <p
                  id="dpdp-banner-desc"
                  className="text-xs sm:text-sm text-gray-600 leading-relaxed"
                >
                  Lakshmi Venkatesan Charitable Trust values your privacy. In accordance with India&apos;s{' '}
                  <strong className="text-gray-800 font-semibold">
                    Digital Personal Data Protection (DPDP) Act, 2023
                  </strong>
                  , we require your consent for certain optional data processing. We use strictly necessary cookies to ensure secure navigation and form submissions. With your permission, we also use optional website analytics and communications. You can manage or withdraw consent anytime. Learn more in our{' '}
                  <Link to="/privacy-policy" className="text-primary font-semibold underline hover:text-primary-dark">
                    Privacy Policy &amp; DPDPA Notice
                  </Link>.
                </p>
              </div>

              {/* Action Buttons: [ Accept All ]   [ Reject All ]   [ Manage Preferences ] */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0">
                {/* Accept All */}
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  id="dpdp-banner-accept"
                  data-dpdp-action="accept"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg bg-emerald-600 text-white border-2 border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm text-center min-w-[110px]"
                >
                  Accept All
                </button>

                {/* Reject All - Equal level of prominence to Accept All */}
                <button
                  type="button"
                  onClick={handleRejectAll}
                  id="dpdp-banner-reject"
                  data-dpdp-action="reject"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg bg-red-600 text-white border-2 border-red-600 hover:bg-red-700 hover:border-red-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm text-center min-w-[110px]"
                >
                  Reject All
                </button>

                {/* Manage Preferences */}
                <button
                  type="button"
                  onClick={handleOpenPreferencesModal}
                  id="dpdp-banner-preferences"
                  data-dpdp-action="preferences"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg border-2 border-primary/40 bg-gray-50 text-primary hover:bg-primary/10 hover:border-primary active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-1.5 shadow-sm min-w-[140px]"
                >
                  <SlidersIcon className="w-3.5 h-3.5" />
                  <span>Manage Preferences</span>
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* 2. Rendered Privacy Notice & Granular Consent Modal (#revealed-modal) */}
      {showPreferences && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dpdp-modal-title"
          aria-describedby="dpdp-modal-desc"
          id="revealed-modal"
          data-dpdp="revealed-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                  <ShieldIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 id="dpdp-modal-title" className="text-base sm:text-lg font-bold text-gray-900">
                    Privacy Notice &amp; Consent Preferences
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] sm:text-xs text-gray-500">
                    <span>Lakshmi Venkatesan Charitable Trust</span>
                    <span>•</span>
                    <span className="font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      DPDP Act (2023) Notice
                    </span>
                    <span>•</span>
                    <span className="text-gray-600 font-medium">
                      Last updated: September 29, 2026
                    </span>
                    <span>•</span>
                    <span className="text-gray-500">
                      Notice Version: 2.1
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none"
                aria-label="Close privacy notice"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Comprehensive Compliance Sections & Purpose Toggles */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-6 text-sm text-gray-700 leading-relaxed">
              <p id="dpdp-modal-desc" className="text-xs sm:text-sm text-gray-600">
                This notice is provided in compliance with Section 5 and Rule 3 of the Digital Personal Data Protection (DPDP) Act, 2023. Review our data processing practices below and customize your consent preferences. You can also view our full{' '}
                <Link
                  to="/privacy-policy"
                  onClick={handleCloseModal}
                  className="text-primary font-semibold underline hover:text-primary-dark"
                >
                  Privacy Policy &amp; DPDPA Notice
                </Link>.
              </p>

              {/* 1. PLAIN-LANGUAGE SUMMARY (PNA-10: MANDATORY AT TOP) */}
              <section
                aria-labelledby="heading-plain-summary"
                className="p-4 sm:p-5 rounded-xl border border-amber-300 bg-amber-50/80 shadow-sm"
              >
                <div className="flex items-center space-x-2 text-amber-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-amber-500/20 text-amber-800 rounded">
                    <InfoIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-plain-summary">Plain-Language Summary</h3>
                </div>
                <p className="text-xs text-amber-950/80 mb-3 leading-relaxed">
                  In compliance with Rule 3 of the DPDP Act 2023 (Intelligibility Requirement), here is a simple and clear overview of how we handle your personal data:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white/90 p-3 rounded-lg border border-amber-200">
                    <strong className="text-gray-900 block mb-1">What data is collected:</strong>
                    <span className="text-gray-700">
                      Contact information (name, email, mobile number, address), usage/device data (IP address, browser type, cookies), and account/donor information.
                    </span>
                  </div>
                  <div className="bg-white/90 p-3 rounded-lg border border-amber-200">
                    <strong className="text-gray-900 block mb-1">Why it is collected:</strong>
                    <span className="text-gray-700">
                      To manage charitable donations and volunteer onboarding, respond to support inquiries, secure the platform, analyze site reach, and deliver optional updates.
                    </span>
                  </div>
                  <div className="bg-white/90 p-3 rounded-lg border border-amber-200">
                    <strong className="text-gray-900 block mb-1">How it is used:</strong>
                    <span className="text-gray-700">
                      Used strictly for specified charitable and operational purposes. We never sell, rent, or trade your personal data, nor engage in cross-site tracking or profiling.
                    </span>
                  </div>
                  <div className="bg-white/90 p-3 rounded-lg border border-amber-200">
                    <strong className="text-gray-900 block mb-1">How users can exercise their rights:</strong>
                    <span className="text-gray-700">
                      You can access, correct, or erase your data, withdraw consent, or designate a nominee anytime using the controls below or by contacting our Grievance Officer: Sanjay Prakash at{' '}
                      <a href="mailto:sanjay@bcbuzz.tech" className="text-primary font-semibold underline">
                        sanjay@bcbuzz.tech
                      </a>.
                    </span>
                  </div>
                </div>
              </section>

              {/* 2. PURPOSES FOR PROCESSING PERSONAL DATA (PNA-02) */}
              <section
                aria-labelledby="heading-purposes"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-gray-50/60"
              >
                <div className="flex items-center space-x-2 text-gray-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-primary/10 text-primary rounded">
                    <TargetIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-purposes">Purposes for Processing Personal Data</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  Under Section 5(1) of the DPDP Act 2023, Lakshmi Venkatesan Charitable Trust processes personal data strictly for the following specified and itemised purposes:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-700">
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Account management:</strong>
                      Managing donor profiles, contribution history, Section 80G tax certification records, and volunteer registrations.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Customer support:</strong>
                      Responding to citizen inquiries, evaluating humanitarian support requests, and resolving beneficiary grievances.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Security and fraud prevention:</strong>
                      Securing web forms, safeguarding server infrastructure, preventing automated spam, and authenticating donation pledges.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Product/service analytics:</strong>
                      Evaluating website performance, accessibility, and community engagement to improve our public charitable programs.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Personalisation:</strong>
                      Preserving your specific cookie consent choices and language or display preferences.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-primary font-bold text-base leading-none">•</span>
                    <div>
                      <strong className="text-gray-900 block">Marketing communications:</strong>
                      Sending optional newsletters, charitable impact reports, and upcoming community event updates upon your active consent.
                    </div>
                  </li>
                </ul>
              </section>

              {/* 3. PERSONAL DATA WE COLLECT (PNA-03) */}
              <section
                aria-labelledby="heading-categories"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-white"
              >
                <div className="flex items-center space-x-2 text-gray-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-secondary/10 text-secondary rounded">
                    <FolderIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-categories">Personal Data We Collect</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  In accordance with Section 5 and Rule 3 of the DPDP Act 2023, we collect only necessary personal data categorized as follows:
                </p>
                <ul className="space-y-2.5 text-xs text-gray-700">
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900">Contact information:</strong> Full name, email address, telephone/mobile number, and mailing address submitted voluntarily via our contact, donation, volunteer, or support forms.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900">Usage/device information:</strong> IP address, device type, operating system, browser metadata, and essential session identifiers for site reliability, security, and traffic performance.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900">Account information:</strong> Donor contribution records, statutory tax identifier (PAN for Section 80G tax receipts where requested), volunteer qualifications, and recorded privacy consent preferences.
                  </li>
                </ul>
              </section>

              {/* 4. DATA RETENTION (PNA-04) */}
              <section
                aria-labelledby="heading-retention"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-gray-50/60"
              >
                <div className="flex items-center space-x-2 text-gray-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-amber-500/10 text-amber-600 rounded">
                    <ClockIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-retention">Data Retention</h3>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  We retain personal data strictly for: <strong className="text-gray-900 font-semibold">36 months from contract termination</strong>. Data is erased upon fulfillment of the processing purpose or withdrawal of consent in accordance with Section 8(7) of the DPDP Act, 2023, except where a longer retention period is mandated by applicable statutory or tax laws.
                </p>
              </section>

              {/* 5. YOUR PRIVACY RIGHTS (PNA-06) */}
              <section
                aria-labelledby="heading-rights"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-white"
              >
                <div className="flex items-center space-x-2 text-gray-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-blue-500/10 text-blue-600 rounded">
                    <ShieldIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-rights">Your Privacy Rights</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  Under Sections 11–14 of India&apos;s Digital Personal Data Protection Act 2023, you possess the following enforceable statutory rights:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-700 mb-3">
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900 block mb-0.5">Right to Access:</strong>
                    Obtain a summary of personal data processed and third parties with whom data has been shared.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900 block mb-0.5">Right to Correction:</strong>
                    Correct, complete, or update any inaccurate or outdated personal data.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900 block mb-0.5">Right to Erasure:</strong>
                    Request erasure of personal data that is no longer necessary for the specified purpose.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <strong className="text-gray-900 block mb-0.5">Right to Withdraw Consent:</strong>
                    Withdraw your consent at any time as easily as giving it (§6(4)), without adverse penalty.
                  </li>
                  <li className="bg-gray-50 p-3 rounded-lg border border-gray-200 sm:col-span-2">
                    <strong className="text-gray-900 block mb-0.5">Right to Nominate:</strong>
                    Nominate any individual who will exercise data principal rights in the event of death or incapacity.
                  </li>
                </ul>
                <div className="text-xs text-gray-600 bg-blue-50/70 p-3 rounded-lg border border-blue-100 leading-relaxed">
                  <strong className="text-gray-900">Valid Mechanism for Exercising Rights:</strong> You may adjust consent choices anytime via the controls below or by contacting our designated Grievance Officer: <strong className="text-gray-900">Sanjay Prakash</strong> at <a href="mailto:sanjay@bcbuzz.tech" className="text-primary font-semibold underline">sanjay@bcbuzz.tech</a> or our trust support at <a href="mailto:hello@mylvtrust.com" className="text-primary font-semibold underline">hello@mylvtrust.com</a> / <span className="font-semibold">+91 9942019000</span>.
                </div>
              </section>

              {/* 6. GRIEVANCE REDRESSAL & DPBI ESCALATION (PNA-09) */}
              <section
                aria-labelledby="heading-dpbi"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-gray-50/60"
              >
                <div className="flex items-center space-x-2 text-gray-900 font-bold text-sm sm:text-base mb-2">
                  <span className="p-1 bg-rose-500/10 text-rose-600 rounded">
                    <ScaleIcon className="w-4 h-4" />
                  </span>
                  <h3 id="heading-dpbi">Grievance Redressal &amp; DPBI Escalation</h3>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  If any privacy grievance or request submitted to our Grievance Officer remains unresolved after 30 days, or if you are dissatisfied with our response, you have the statutory right under Section 13 and Rule 3(b) of the Digital Personal Data Protection Act, 2023 to escalate your grievance to the <strong className="text-gray-900 font-semibold">Data Protection Board of India</strong>.
                </p>
              </section>

              {/* 7. NOTICE TIMING & POINT OF COLLECTION (PNA-12) */}
              <section
                aria-labelledby="heading-timing"
                className="p-4 sm:p-5 rounded-xl border border-gray-200 bg-white"
              >
                <h3 id="heading-timing" className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1">
                  Point-of-Collection Presentation (DPDP Act §5(1))
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  This Privacy Notice is presented or linked at or before the time personal data is collected across our contact, volunteer, support request, and donation forms, guaranteeing informed consent prior to data processing.
                </p>
              </section>

              {/* 8. GRANULAR PURPOSE TOGGLES & CONSENT CONTROLS */}
              <div className="space-y-4 pt-2 border-t border-gray-200">
                <h3 className="text-sm font-bold text-gray-900">
                  Granular Consent Preferences
                </h3>

                {/* Purpose 1: Strictly Necessary */}
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-primary"><LockIcon className="w-4 h-4" /></span>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Strictly Necessary / Core Operations
                        </h4>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                          Always Active
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed mt-1">
                        Essential for site security, session routing, secure form submissions (Donation pledge, Volunteer, Contact, and Support Requests), and recording your privacy preferences. These cannot be disabled.
                      </p>
                    </div>
                    <div className="relative inline-flex items-center cursor-not-allowed">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        aria-label="Strictly Necessary Cookies - Always Active"
                        className="sr-only"
                      />
                      <div className="w-11 h-6 bg-primary rounded-full transition-colors opacity-90 flex items-center px-1">
                        <div className="w-4 h-4 bg-white rounded-full transform translate-x-5 shadow-sm"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Purpose 2: Website Analytics */}
                <div className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-secondary"><AnalyticsIcon className="w-4 h-4" /></span>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Website Analytics &amp; Performance
                        </h4>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                          Optional
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed mt-1">
                        Helps us understand how visitors interact with our charitable platform so we can improve site navigation and outreach. No cross-site tracking or profiling is conducted.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="dpdp-toggle-analytics"
                        checked={toggles.analytics}
                        onChange={() => handleToggleChange('analytics')}
                        aria-label="Toggle Website Analytics"
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>

                {/* Purpose 3: Communications & Updates */}
                <div className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-blue-600"><EnvelopeIcon className="w-4 h-4" /></span>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Communications &amp; Updates
                        </h4>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                          Optional
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed mt-1">
                        Allows us to send periodic organizational newsletters, charitable impact reports, and event notifications when you subscribe through our website.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="dpdp-toggle-communications"
                        checked={toggles.communications}
                        onChange={() => handleToggleChange('communications')}
                        aria-label="Toggle Communications and Updates"
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Active Consent Record Details (if already given) */}
              {currentConsent && (
                <div className="pt-2 text-[11px] text-gray-500 border-t border-gray-200 flex flex-wrap justify-between items-center gap-2">
                  <span>
                    Record ID: <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-mono">{currentConsent.consentId}</code>
                  </span>
                  <span>
                    Consent Timestamp:{' '}
                    {new Date(currentConsent.timestamp).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}

              {/* Notice Metadata (PNA-11) */}
              <div className="text-center pt-2 text-[11px] text-gray-500 border-t border-gray-100 space-y-0.5">
                <p>
                  <strong>Last updated: September 29, 2026</strong> • Notice Version: 2.1 • ID: <code>6439c25a-00d9-416a-b622-16bdfb4730ef</code>
                </p>
                <p>Maintained by Lakshmi Venkatesan Charitable Trust • DPDP Act 2023 §5 &amp; Rule 3 Compliant</p>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Withdrawal action */}
              <div>
                {currentConsent && (
                  <button
                    type="button"
                    onClick={handleWithdrawAll}
                    id="dpdp-withdraw-consent"
                    className="text-xs font-semibold text-red-600 hover:text-red-800 hover:underline flex items-center space-x-1.5 py-1 focus:outline-none"
                  >
                    <UndoIcon className="w-3.5 h-3.5" />
                    <span>Withdraw All Consent</span>
                  </button>
                )}
              </div>

              {/* Primary Choices */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  id="dpdp-modal-reject"
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg border-2 border-red-600 bg-red-600 text-white hover:bg-red-700 active:scale-95 transition-all text-center shadow-sm"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  id="dpdp-save-preferences"
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg bg-secondary text-white hover:bg-secondary-dark active:scale-95 transition-all text-center shadow-sm"
                >
                  Save Choices
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  id="dpdp-modal-accept"
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg border-2 border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95 transition-all text-center shadow-sm"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;
